import { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useGlobalReducer } from "../../../GlobalContext";
import { AdvancedSearchType } from "../../config/enums";
import { addSuccessNotification } from "../../shell/notification/notifications";
import { PicklistField } from "../../blocks/fields/PicklistField";
import { TextField } from "../../blocks/fields/TextField";
import { Button } from "../../elements/Button";
import { CancelButton } from "../../elements/CancelButton";
import { ConfirmationModal } from "../../elements/ConfirmationModal";
import { Loading } from "../../elements/Loading";
import { Modal } from "../../elements/Modal";
import { DeleteIcon, EditIcon, MagnifyIcon } from "../../icons";
import "../../lists/ListRowActions.scss";
import { SavedViewNameEditor } from "./SavedViewNameEditor";
import {
  SavedViewSummary,
  deleteSavedView,
  listSavedViews,
  renameSavedView,
} from "./savedViewService";
import { LoadSavedViewModalProps } from "./LoadSavedViewModal.types";

/** Picks one of the saved views for an entity, which can be renamed or deleted from here. */
export function LoadSavedViewModal({
  entityName,
  allowedEntityTypes,
  canManage,
  onLoad,
  onClose,
}: LoadSavedViewModalProps) {
  const globalDispatch = useGlobalReducer();
  const [views, setViews] = useState<SavedViewSummary[] | null>(null);
  const [entity, setEntity] = useState(entityName);
  const [nameFilter, setNameFilter] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [renaming, setRenaming] = useState<SavedViewSummary | null>(null);
  const [deleting, setDeleting] = useState<SavedViewSummary | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const entityOptions = useMemo(
    () =>
      Object.entries(allowedEntityTypes ?? AdvancedSearchType).map(([Value, Key]) => ({
        Key,
        Value,
      })),
    [allowedEntityTypes]
  );

  const refresh = useCallback(
    () => listSavedViews(globalDispatch).then(setViews),
    [globalDispatch]
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  const shown = (views ?? []).filter(
    (view) =>
      (!entity || view.EntityName.toLowerCase() === entity.toLowerCase()) &&
      view.Name.toLowerCase().includes(nameFilter.trim().toLowerCase())
  );

  const load = async () => {
    setIsSubmitting(true);

    if (await onLoad(selectedId)) {
      onClose();
      return;
    }

    setIsSubmitting(false);
  };

  const rename = async (name: string) => {
    if (!renaming) {
      return;
    }

    if (name === renaming.Name) {
      setRenaming(null);
      return;
    }

    setIsSubmitting(true);

    if (await renameSavedView(globalDispatch, renaming.Id, name)) {
      addSuccessNotification(globalDispatch, `Saved view renamed to ${name}`);
      await refresh();
      setRenaming(null);
    }

    setIsSubmitting(false);
  };

  const confirmDelete = async () => {
    setIsSubmitting(true);

    if (deleting && (await deleteSavedView(globalDispatch, deleting.Id))) {
      if (selectedId === deleting.Id) {
        setSelectedId("");
      }
      await refresh();
      setDeleting(null);
    }

    setIsSubmitting(false);
  };

  return (
    <>
      <Modal title="Load View" onClose={onClose} className="modal-steady-height">
        <div className="modal-body">
          <PicklistField
            label="Entity"
            name="saved-view-entity"
            value={entity}
            options={entityOptions}
            onChange={(value) => setEntity(value ?? "")}
          />
          {views === null ? (
            <Loading />
          ) : views.length === 0 ? (
            <div className="alert alert-warning">No saved views found</div>
          ) : (
            <>
              <div className="position-relative">
                <TextField
                  label="Search"
                  name="saved-view-search"
                  value={nameFilter}
                  onChange={setNameFilter}
                  placeholder="Filter by name"
                />
              </div>
              <ul className="list-group rounded-2">
                {shown.map((view) => (
                  <li key={view.Id} className="list-group-item d-flex align-items-center gap-2">
                    {renaming?.Id === view.Id ? (
                      <SavedViewNameEditor
                        name={view.Name}
                        isSaving={isSubmitting}
                        onApply={rename}
                        onCancel={() => setRenaming(null)}
                      />
                    ) : (
                      <label className="flex-grow-1 cursor-pointer">
                        <input
                          className="form-check-input me-3"
                          type="radio"
                          name="saved-view"
                          checked={selectedId === view.Id}
                          onChange={() => setSelectedId(view.Id)}
                        />
                        {view.Name}
                      </label>
                    )}
                    {canManage && renaming?.Id !== view.Id && (
                      <>
                        <button
                          type="button"
                          className="list-row-action"
                          title={`Rename ${view.Name}`}
                          aria-label={`Rename ${view.Name}`}
                          onClick={() => setRenaming(view)}
                        >
                          <FontAwesomeIcon icon={EditIcon} />
                        </button>
                        <button
                          type="button"
                          className="list-row-action hover-text-danger"
                          title={`Delete ${view.Name}`}
                          aria-label={`Delete ${view.Name}`}
                          onClick={() => setDeleting(view)}
                        >
                          <FontAwesomeIcon icon={DeleteIcon} />
                        </button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
              {shown.length === 0 && <p className="text-muted mt-2 mb-0">No views match.</p>}
            </>
          )}
        </div>
        <div className="modal-footer d-block">
          <div className="w-100 d-flex justify-content-between">
            <CancelButton onClick={onClose} />
            <Button
              text="Load"
              variant="primary"
              icon={MagnifyIcon}
              onClick={load}
              showSpinner={isSubmitting}
              disabled={!selectedId || isSubmitting}
            />
          </div>
        </div>
      </Modal>
      {deleting && (
        <ConfirmationModal
          modalTitle="Delete saved view"
          isSubmitting={isSubmitting}
          onClose={() => !isSubmitting && setDeleting(null)}
          onConfirm={confirmDelete}
        >
          Are you sure you want to delete {deleting.Name}?
        </ConfirmationModal>
      )}
    </>
  );
}
